import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { difference } from 'lodash';
import { useEffect, useState } from 'react';
import { CompanyWidgetId, CompanyWidgetsOrderConfig, Container } from '../../pages/Company/Company.constants';
import useOnDrag, { useOnDragResult } from '../common/useOnDrag';

export interface CompanyDraggableState {
  leftSideWidgets: CompanyWidgetId[];
  middleWidgets: CompanyWidgetId[];
  rightSideWidgets: CompanyWidgetId[];
  activeContainer: Container | null;
}

const getDefaultCompanyWidgets = (columnView: number, defaultConfig: CompanyWidgetsOrderConfig) =>
  columnView === 1
    ? {
        leftSideWidgets: [],
        middleWidgets: [
          ...defaultConfig.leftSideWidgets,
          ...defaultConfig.middleWidgets,
          ...defaultConfig.rightSideWidgets,
        ],
        rightSideWidgets: [],
      }
    : defaultConfig;

const useOnDragCompany = (
  columnView: number,
  companyWidgetsKey: string,
  defaultConfig: CompanyWidgetsOrderConfig
): useOnDragResult<CompanyDraggableState> => {
  const defaultCompanyWidgets = getDefaultCompanyWidgets(columnView, defaultConfig);

  const findContainer = (id, items) => Object.keys(items).find((key) => items[key].includes(id));
  const [activeContainer, setActiveContainer] = useState<Container | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveContainer(event.active.data.current?.sortable.containerId);
  };

  const handleDragOver = (event: DragOverEvent, draggableState, setDraggableState) => {
    const { over, active } = event;
    const overId = over?.id; // widget or panel id

    if (!overId || active.id in draggableState) {
      return;
    }

    const isOverPanelOnly = draggableState[overId];

    const overContainer = isOverPanelOnly ? overId : findContainer(overId, draggableState);
    const activeContainer = findContainer(active.id, draggableState);
    if (
      !overContainer ||
      !activeContainer ||
      activeContainer === Container.MIDDLE ||
      overContainer === Container.MIDDLE
    ) {
      return;
    }

    if (isOverPanelOnly) {
      setDraggableState({
        ...draggableState,
        [overContainer]: [...draggableState[overContainer].filter((item) => item !== active.id), active.id],
      });
    }

    if (activeContainer !== overContainer) {
      const activeContainerWidgets = draggableState[activeContainer];
      const overContainerWidgets = draggableState[overContainer];
      const overWidgetIndex = overContainerWidgets.indexOf(overId);
      const activeWidgetIndex = activeContainerWidgets.indexOf(active.id);
      let newIndex: number;
      if (overId in draggableState) {
        newIndex = overContainerWidgets.length + 1;
      } else {
        const isBelowOverItem =
          over &&
          active.rect.current.translated &&
          active.rect.current.translated.top > over.rect.top + over.rect.height;
        const modifier = isBelowOverItem ? 1 : 0;
        newIndex = overWidgetIndex >= 0 ? overWidgetIndex + modifier : overContainerWidgets.length + 1;
      }
      setDraggableState({
        ...draggableState,
        [activeContainer]: draggableState[activeContainer].filter((item) => item !== active.id),
        [overContainer]: [
          ...draggableState[overContainer].slice(0, newIndex),
          draggableState[activeContainer][activeWidgetIndex],
          ...draggableState[overContainer].slice(newIndex, draggableState[overContainer].length),
        ],
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent, draggableState, setDraggableState) => {
    const { over, active } = event;
    const activeContainer = findContainer(active.id, draggableState);
    const overId = over?.id;

    if (!activeContainer || !overId) {
      setActiveContainer(null);
      return;
    }
    const overContainer = findContainer(overId, draggableState);

    if (overContainer && overContainer === activeContainer) {
      const activeWidgetIndex = draggableState[activeContainer].indexOf(active.id);
      const overWidgetIndex = draggableState[overContainer].indexOf(overId);

      if (activeWidgetIndex !== overWidgetIndex) {
        setDraggableState({
          ...draggableState,
          [overContainer]: arrayMove(draggableState[overContainer], activeWidgetIndex, overWidgetIndex),
        });
      }
    }
    setActiveContainer(null);
  };

  const { onDragStart, onDragEnd, onDragOver, draggableState, resetDraggableState } = useOnDrag(
    companyWidgetsKey,
    defaultCompanyWidgets,
    { onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd }
  );

  useEffect(() => {
    if (!draggableState) {
      return;
    }

    const stateWidgets = [
      ...draggableState.leftSideWidgets,
      ...draggableState.middleWidgets,
      ...draggableState.rightSideWidgets,
    ].sort();
    const defaultWidgets = [
      ...defaultCompanyWidgets.leftSideWidgets,
      ...defaultCompanyWidgets.middleWidgets,
      ...defaultCompanyWidgets.rightSideWidgets,
    ].sort();

    if (stateWidgets.length !== defaultWidgets.length || difference(defaultWidgets, stateWidgets).length > 0) {
      resetDraggableState();
    }
  }, [draggableState, defaultCompanyWidgets]);

  return {
    onDragStart,
    onDragEnd,
    onDragOver,
    draggableState: { ...draggableState, activeContainer },
    resetDraggableState,
  };
};

export default useOnDragCompany;
